import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setProfile } from '../store/userSlice';
import { updateMyProfile } from '../services/profile';
import {
  getPostAuthHomeOrQuestionnaire,
  readMedicalQuestionnaireCompletedFromStorage,
} from '../utils/signInRedirect';

export default function OnboardingChangePasswordCard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!password || !confirmPassword) {
      setError('Please fill in all required fields.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await updateMyProfile({ password, must_change_password: false });
      dispatch(setProfile({ must_change_password: false }));
      const userRole = localStorage.getItem('userRole') || 'Patient';
      const viewAsRole = localStorage.getItem('supervisorViewAsRole');
      const homePath = getPostAuthHomeOrQuestionnaire(
        readMedicalQuestionnaireCompletedFromStorage(),
        userRole,
        viewAsRole
      );
      navigate(homePath, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to update password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="bg-white rounded-2xl border border-gray-200 shadow-sm w-full max-w-[660px] p-5 md:p-[60px] overflow-hidden"
      style={{ position: 'relative', zIndex: 1 }}
    >
      <div className="flex flex-col items-center gap-3 w-full">
        <p className="text-[#2B3133] font-libreBaskerville text-3xl md:text-[40px] leading-[1em] text-center">
          Set New Password
        </p>
        <p className="text-[#889696] font-poppins text-sm md:text-base font-normal text-center mt-[1px] md:mt-0">
          Password must be at least 8 characters long. Please enter your new password below
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6 mt-10">
        <div className="flex flex-col items-center gap-[30px] w-full">
          <div className="flex flex-col justify-center items-start gap-2 w-full md:w-[540.51px] h-[85px] relative">
            <label className="text-[#889696] font-poppins text-sm font-medium">
              New Password
            </label>
            <div className="mt-1 flex items-center w-full md:w-[540.51px] h-[60px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8]">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="@#*%"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="flex-1 bg-transparent outline-none px-5 font-poppins"
                required
              />
              <div className="w-px h-10 bg-[#CFD9E0]" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="pl-3 pr-4 cursor-pointer hover:opacity-70 transition-opacity"
              >
                {showPassword ? (
                  <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
                    <path d="M12.5 4.5C7.5 4.5 3.73 7.61 2.5 12C3.73 16.39 7.5 19.5 12.5 19.5C17.5 19.5 21.27 16.39 22.5 12C21.27 7.61 17.5 4.5 12.5 4.5ZM12.5 17C9.74 17 7.5 14.76 7.5 12C7.5 9.24 9.74 7 12.5 7C15.26 7 17.5 9.24 17.5 12C17.5 14.76 15.26 17 12.5 17ZM12.5 9C10.84 9 9.5 10.34 9.5 12C9.5 13.66 10.84 15 12.5 15C14.16 15 15.5 13.66 15.5 12C15.5 10.34 14.16 9 12.5 9Z" fill="#889696" />
                    <path d="M2 2L22 22" stroke="#889696" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
                    <path d="M10.2276 12C10.2276 12.6962 10.5041 13.3639 10.9964 13.8562C11.4887 14.3484 12.1564 14.625 12.8526 14.625C13.5487 14.625 14.2164 14.3484 14.7087 13.8562C15.201 13.3639 15.4776 12.6962 15.4776 12C15.4776 11.3038 15.201 10.6361 14.7087 10.1438C14.2164 9.65156 13.5487 9.375 12.8526 9.375C12.1564 9.375 11.4887 9.65156 10.9964 10.1438C10.5041 10.6361 10.2276 11.3038 10.2276 12ZM23.0291 11.3953C20.8072 6.71484 17.4486 4.35938 12.9463 4.35938C8.44161 4.35938 5.08537 6.71484 2.86349 11.3977C2.77437 11.5864 2.72815 11.7925 2.72815 12.0012C2.72815 12.2099 2.77437 12.416 2.86349 12.6047C5.08537 17.2852 8.44396 19.6406 12.9463 19.6406C17.451 19.6406 20.8072 17.2852 23.0291 12.6023C23.2096 12.2227 23.2096 11.782 23.0291 11.3953ZM12.8526 16.125C10.5744 16.125 8.72755 14.2781 8.72755 12C8.72755 9.72188 10.5744 7.875 12.8526 7.875C15.1307 7.875 16.9776 9.72188 16.9776 12C16.9776 14.2781 15.1307 16.125 12.8526 16.125Z" fill="#889696" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="flex flex-col justify-center items-start gap-2 w-full md:w-[540.51px] h-[85px] relative">
            <label className="text-[#889696] font-poppins text-sm font-medium">
              New Password Confirmation
            </label>
            <div className="mt-1 flex items-center w-full md:w-[540.51px] h-[60px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8]">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="@#*%"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="flex-1 bg-transparent outline-none px-5 font-poppins"
                required
              />
              <div className="w-px h-10 bg-[#CFD9E0]" />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="pl-3 pr-4 cursor-pointer hover:opacity-70 transition-opacity"
              >
                {showConfirmPassword ? (
                  <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
                    <path d="M12.5 4.5C7.5 4.5 3.73 7.61 2.5 12C3.73 16.39 7.5 19.5 12.5 19.5C17.5 19.5 21.27 16.39 22.5 12C21.27 7.61 17.5 4.5 12.5 4.5ZM12.5 17C9.74 17 7.5 14.76 7.5 12C7.5 9.24 9.74 7 12.5 7C15.26 7 17.5 9.24 17.5 12C17.5 14.76 15.26 17 12.5 17ZM12.5 9C10.84 9 9.5 10.34 9.5 12C9.5 13.66 10.84 15 12.5 15C14.16 15 15.5 13.66 15.5 12C15.5 10.34 14.16 9 12.5 9Z" fill="#889696" />
                    <path d="M2 2L22 22" stroke="#889696" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
                    <path d="M10.2276 12C10.2276 12.6962 10.5041 13.3639 10.9964 13.8562C11.4887 14.3484 12.1564 14.625 12.8526 14.625C13.5487 14.625 14.2164 14.3484 14.7087 13.8562C15.201 13.3639 15.4776 12.6962 15.4776 12C15.4776 11.3038 15.201 10.6361 14.7087 10.1438C14.2164 9.65156 13.5487 9.375 12.8526 9.375C12.1564 9.375 11.4887 9.65156 10.9964 10.1438C10.5041 10.6361 10.2276 11.3038 10.2276 12ZM23.0291 11.3953C20.8072 6.71484 17.4486 4.35938 12.9463 4.35938C8.44161 4.35938 5.08537 6.71484 2.86349 11.3977C2.77437 11.5864 2.72815 11.7925 2.72815 12.0012C2.72815 12.2099 2.77437 12.416 2.86349 12.6047C5.08537 17.2852 8.44396 19.6406 12.9463 19.6406C17.451 19.6406 20.8072 17.2852 23.0291 12.6023C23.2096 12.2227 23.2096 11.782 23.0291 11.3953ZM12.8526 16.125C10.5744 16.125 8.72755 14.2781 8.72755 12C8.72755 9.72188 10.5744 7.875 12.8526 7.875C15.1307 7.875 16.9776 9.72188 16.9776 12C16.9776 14.2781 15.1307 16.125 12.8526 16.125Z" fill="#889696" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {error ? (
          <p className="text-red-500 text-sm font-poppins -mt-2">{error}</p>
        ) : null}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-14 rounded-full bg-[#119975] text-[#FFEBDB] font-poppins text-lg font-medium mt-4 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          {isSubmitting ? 'Saving...' : 'Save & Continue'}
        </button>

      </form>
    </div>
  );
}
